import { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

// MCP discovery (`/.well-known/...`) and the `/mcp/` endpoint live at the
// origin root, not under any `/api` path. Derive the bare origin so the
// catalog URL works regardless of whether NEXT_PUBLIC_API_URL has an `/api`
// suffix (it does on dev/staging/prod) or not (local).
function siteOrigin(apiBase) {
  try {
    const u = new URL(apiBase, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    return u.origin;
  } catch (_) {
    return apiBase.replace(/\/api\/?$/, '').replace(/\/$/, '');
  }
}

const TABS = [
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'inspector', label: 'MCP Inspector' },
  { id: 'stdio', label: 'stdio shim' },
];

function CodeBlock({ children, language = 'json' }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto border border-gray-800">
        <code>{children}</code>
      </pre>
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

export default function MCPPage() {
  const [catalog, setCatalog] = useState(null);
  const [tab, setTab] = useState('claude-desktop');
  const [error, setError] = useState(null);

  useEffect(() => {
    const origin = siteOrigin(API_BASE);
    fetch(`${origin}/.well-known/mcp-catalog.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setCatalog)
      .catch((e) => setError(e.message));
  }, []);

  const origin = siteOrigin(API_BASE);
  const serverUrl = catalog?.endpoint?.startsWith('http')
    ? catalog.endpoint
    : `${origin}${catalog?.endpoint || '/mcp/'}`;

  const claudeDesktopJson = JSON.stringify(
    {
      mcpServers: {
        creolecentric: {
          type: 'http',
          url: serverUrl,
          headers: { Authorization: 'Bearer cc_<your_keyid>_<your_secret>' },
        },
      },
    },
    null,
    2
  );

  const claudeCodeCmd = `claude mcp add --transport http creolecentric ${serverUrl} \\
  --header "Authorization: Bearer cc_<your_keyid>_<your_secret>"`;

  const cursorJson = JSON.stringify(
    {
      mcpServers: {
        creolecentric: {
          url: serverUrl,
          headers: { Authorization: 'Bearer cc_<your_keyid>_<your_secret>' },
        },
      },
    },
    null,
    2
  );

  const inspectorCmd = `npx @modelcontextprotocol/inspector \\
  --transport http \\
  --url ${serverUrl} \\
  --header "Authorization: Bearer cc_<your_keyid>_<your_secret>"`;

  const stdioInstall = `pip install creolecentric-mcp
export CREOLECENTRIC_API_KEY=cc_<your_keyid>_<your_secret>
creolecentric-mcp  # speaks MCP over stdio`;

  const stdioConfig = JSON.stringify(
    {
      mcpServers: {
        creolecentric: {
          command: 'creolecentric-mcp',
          env: { CREOLECENTRIC_API_KEY: 'cc_<your_keyid>_<your_secret>' },
        },
      },
    },
    null,
    2
  );

  const tools = catalog?.tools || [];

  return (
    <>
      <Head>
        <title>MCP Server — CreoleCentric Developer</title>
        <meta
          name="description"
          content="Connect CreoleCentric to Claude, Cursor, and other Model Context Protocol clients."
        />
      </Head>

      <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-2">
            <MobileBackButton />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              MCP Server
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Plug CreoleCentric into Claude, Cursor, and any other client that
            speaks the{' '}
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Model Context Protocol
            </a>
            . Tools mirror the existing REST API: TTS, translation, dictionary
            lookup, speech-to-text, and account/credits.
          </p>

          {/* Server URL & creds card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Server URL
                </h2>
                <div className="font-mono text-sm bg-gray-100 dark:bg-gray-900 rounded px-3 py-2 break-all border border-gray-200 dark:border-gray-700">
                  {serverUrl}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Streamable HTTP transport (MCP spec 2024-11-05+).
                </p>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Authentication
                </h2>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <p>
                    Two methods, both via the standard{' '}
                    <code className="font-mono text-xs">Authorization</code> header:
                  </p>
                  <p>
                    <strong>OAuth 2.1</strong> (recommended for Claude.ai) —
                    handled automatically by your MCP client. You sign in to
                    creolecentric.com and click Authorize on a consent page.
                    No token to copy.
                  </p>
                  <p>
                    <strong>API key</strong> — paste a{' '}
                    <code className="font-mono text-xs">cc_&lt;keyid&gt;_&lt;secret&gt;</code>{' '}
                    string from{' '}
                    <Link href="/api-keys" className="text-blue-600 dark:text-blue-400 hover:underline">
                      /api-keys
                    </Link>{' '}
                    into your client config. Best for scripts, Cursor, and
                    Claude Desktop where OAuth isn&apos;t available.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Claude.ai connector walkthrough */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Connect from Claude.ai
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              The fastest way to use CreoleCentric. Claude.ai handles the
              entire OAuth handshake for you — you just click Authorize.
            </p>

            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 mb-5 text-sm text-amber-900 dark:text-amber-200">
              <strong>Subscription required.</strong> The MCP server (and the
              rest of the developer API) is included with{' '}
              <Link href="/subscription" className="underline font-semibold">
                Creator, Pro, Business, and Enterprise plans
              </Link>
              , or with the Creator Pack (85,000+ purchased credits).
            </div>

            <ol className="space-y-4 text-sm text-gray-700 dark:text-gray-300 list-none">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center text-xs">1</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
                    Sign in to creolecentric.com
                  </p>
                  <p>
                    In the same browser you&apos;ll use for Claude.ai, log into{' '}
                    <Link href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
                      creolecentric.com
                    </Link>
                    . This is what tells our server who you are when the
                    consent popup opens.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center text-xs">2</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
                    Add the connector in Claude.ai
                  </p>
                  <p className="mb-2">
                    In Claude.ai, open the connector / custom connector
                    settings and add a new MCP server with this URL:
                  </p>
                  <div className="font-mono text-xs bg-gray-100 dark:bg-gray-900 rounded px-3 py-2 border border-gray-200 dark:border-gray-700 break-all">
                    {serverUrl}
                  </div>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">
                    Leave any &quot;token&quot; or &quot;API key&quot; field
                    empty — Claude.ai handles auth automatically via OAuth.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center text-xs">3</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
                    Click Authorize on the consent page
                  </p>
                  <p>
                    Claude.ai opens a popup pointed at our branded consent
                    page. It lists the requested scopes (e.g. <code className="font-mono text-xs">tts</code>,{' '}
                    <code className="font-mono text-xs">account:read</code>) and the
                    name of the client (&quot;Claude&quot;). Click{' '}
                    <strong>Authorize</strong>; the popup closes and the
                    connector is ready.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center text-xs">4</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
                    Use it in a chat
                  </p>
                  <p className="mb-2">Try one of these prompts:</p>
                  <ul className="space-y-1 text-gray-600 dark:text-gray-400 list-disc list-inside">
                    <li>
                      <em>&quot;Use CreoleCentric to translate &lsquo;Hello, how are you?&rsquo; to Haitian Creole.&quot;</em>
                    </li>
                    <li>
                      <em>&quot;Look up the Haitian word &lsquo;bonjou&rsquo; in CreoleCentric&apos;s dictionary.&quot;</em>
                    </li>
                    <li>
                      <em>&quot;Generate Haitian Creole audio for &lsquo;Bonswa, kijan ou ye?&rsquo; using a male voice.&quot;</em>
                    </li>
                    <li>
                      <em>&quot;What&apos;s my CreoleCentric credit balance?&quot;</em>
                    </li>
                  </ul>
                </div>
              </li>
            </ol>

            <details className="mt-6 text-sm text-gray-700 dark:text-gray-300">
              <summary className="cursor-pointer font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                What happens behind the scenes
              </summary>
              <div className="mt-3 space-y-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                <p>
                  When you add the connector, Claude.ai discovers our auth
                  setup at{' '}
                  <code className="font-mono text-xs">/.well-known/oauth-protected-resource</code>{' '}
                  and{' '}
                  <code className="font-mono text-xs">/.well-known/oauth-authorization-server</code>,
                  registers itself dynamically (RFC 7591), and walks you
                  through the OAuth 2.1 + PKCE consent flow.
                </p>
                <p>
                  After you click Authorize, Claude.ai receives a short-lived
                  access token bound to your CreoleCentric account. Every
                  tool call from Claude.ai uses that token, so credits and
                  rate limits land on your account exactly like REST API
                  usage. Tokens auto-rotate via a refresh token — you only
                  see a consent page once per connector, not per session.
                </p>
                <p>
                  Tokens are <strong>scoped per user</strong>. If a teammate
                  connects from their own browser, their requests bind to
                  their account, not yours.
                </p>
              </div>
            </details>
          </div>

          {/* Quick start */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 mb-8">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 pt-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Quick start
              </h2>
              <div className="flex flex-wrap gap-1 -mb-px">
                {TABS.map((t) => (
                  <TabButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                    {t.label}
                  </TabButton>
                ))}
              </div>
            </div>
            <div className="p-6">
              {tab === 'claude-desktop' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Add this to <code className="font-mono">claude_desktop_config.json</code>{' '}
                    (macOS:{' '}
                    <code className="font-mono">
                      ~/Library/Application Support/Claude/claude_desktop_config.json
                    </code>
                    ) and restart Claude Desktop.
                  </p>
                  <CodeBlock>{claudeDesktopJson}</CodeBlock>
                </>
              )}
              {tab === 'claude-code' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Register the server with Claude Code's CLI:
                  </p>
                  <CodeBlock language="bash">{claudeCodeCmd}</CodeBlock>
                </>
              )}
              {tab === 'cursor' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Add to <code className="font-mono">~/.cursor/mcp.json</code> (or your
                    project's <code className="font-mono">.cursor/mcp.json</code>).
                  </p>
                  <CodeBlock>{cursorJson}</CodeBlock>
                </>
              )}
              {tab === 'inspector' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Sanity-check the server with the official MCP Inspector:
                  </p>
                  <CodeBlock language="bash">{inspectorCmd}</CodeBlock>
                </>
              )}
              {tab === 'stdio' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    For clients that only speak stdio MCP, install our small shim:
                  </p>
                  <CodeBlock language="bash">{stdioInstall}</CodeBlock>
                  <p className="text-sm text-gray-600 dark:text-gray-300 my-3">
                    Then point Claude Desktop (or any stdio MCP client) at it:
                  </p>
                  <CodeBlock>{stdioConfig}</CodeBlock>
                </>
              )}
            </div>
          </div>

          {/* Tool catalog */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Available tools
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Live from the server. Discover the full input schemas at runtime via{' '}
              <code className="font-mono">tools/list</code>.
            </p>
            {error && (
              <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
                Failed to load tool catalog: {error}
              </div>
            )}
            {!error && !catalog && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Loading tool catalog...
              </div>
            )}
            {tools.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {tools.map((name) => (
                  <code
                    key={name}
                    className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded px-2 py-1 border border-gray-200 dark:border-gray-700 font-mono"
                  >
                    {name}
                  </code>
                ))}
              </div>
            )}
          </div>

          {/* Embeddable badge */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Show that you support MCP
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Drop one of these badges in your README, docs site, or marketing
              page so users know your project speaks to CreoleCentric over the
              Model Context Protocol.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <img
                src="https://img.shields.io/badge/MCP-Compatible-2563eb?style=for-the-badge&labelColor=4f46e5"
                alt="MCP Compatible"
                className="h-7"
              />
              <img
                src="https://img.shields.io/badge/Powered_by-CreoleCentric_MCP-2563eb?style=flat&labelColor=111827"
                alt="Powered by CreoleCentric MCP"
                className="h-5"
              />
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                  Markdown
                </label>
                <CodeBlock>{`[![MCP Compatible](https://img.shields.io/badge/MCP-Compatible-2563eb?style=for-the-badge&labelColor=4f46e5)](https://creolecentric.com/developer/mcp)`}</CodeBlock>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                  HTML
                </label>
                <CodeBlock>{`<a href="https://creolecentric.com/developer/mcp"><img src="https://img.shields.io/badge/MCP-Compatible-2563eb?style=for-the-badge&labelColor=4f46e5" alt="MCP Compatible"/></a>`}</CodeBlock>
              </div>
            </div>
          </div>

          {/* Scopes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              API key scopes
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Each tool requires a specific scope on your API key. Pick the
              minimum needed, or use <code className="font-mono">mcp:*</code> for
              everything.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><code className="font-mono text-blue-700 dark:text-blue-300">tts</code> — submit/list/cancel TTS jobs</div>
              <div><code className="font-mono text-blue-700 dark:text-blue-300">tts:read</code> — read-only TTS (voices, status)</div>
              <div><code className="font-mono text-blue-700 dark:text-blue-300">translate</code> — translation tools</div>
              <div><code className="font-mono text-blue-700 dark:text-blue-300">dictionary:read</code> — Haitian↔English lookups</div>
              <div><code className="font-mono text-blue-700 dark:text-blue-300">stt</code> — speech-to-text (not Haitian)</div>
              <div><code className="font-mono text-blue-700 dark:text-blue-300">account:read</code> — balance, subscription, profile</div>
              <div className="sm:col-span-2"><code className="font-mono text-purple-700 dark:text-purple-300">mcp:*</code> — convenience super-scope covering all of the above</div>
            </div>
          </div>

          {/* Limits & roadmap */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Rate limits & billing
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                MCP tool calls share the same per-plan API rate limits and credit
                deductions as REST. See{' '}
                <Link
                  href="/developer/api-stability"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  API stability
                </Link>{' '}
                for the policy.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Claude.ai connector
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                OAuth 2.1 with Dynamic Client Registration is live. Add{' '}
                <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                  https://creolecentric.com/mcp/
                </code>{' '}
                as a Custom Connector in Claude.ai — it auto-discovers and prompts you
                to authorize against your CreoleCentric account. No API key required.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
